import React, { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import { User } from '../types';
import './Login.css';

interface LoginProps {
  setIsAuthenticated: (value: boolean) => void;
  setUser: (user: User | null) => void;
}

const Login: React.FC<LoginProps> = ({ setIsAuthenticated, setUser }) => {
  const navigate = useNavigate();
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showRegister, setShowRegister] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationPhone, setConfirmationPhone] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Remove formatação do telefone (mantém apenas números)
    const cleanPhone = phone.replace(/\D/g, '');
    const phoneWithCountryCode = `+55${cleanPhone}`;

    try {
      const response = await authService.login({
        phone: phoneWithCountryCode,
        password
      });

      if (response.requiresConfirmation) {
        setError('Por favor, confirme seu cadastro através do código enviado por SMS.');
        return;
      }

      authService.setAuth(response.token, response.user);
      setIsAuthenticated(true);
      setUser({
        username: response.user.username || response.user.userId,
      });
      navigate('/my-points');
    } catch (err: any) {
      console.error('Login error:', err);
      
      // Verificar se é erro de usuário não confirmado (403)
      if (err.response?.status === 403 && err.response?.data?.code === 'USER_NOT_CONFIRMED') {
        setConfirmationPhone(phoneWithCountryCode);
        setShowConfirmationModal(true);
        setError('');
      } else {
        setError(err.response?.data?.message || 'Erro ao fazer login. Verifique suas credenciais.');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatPhone = (value: string) => {
    // Remove tudo que não é número
    const numbers = value.replace(/\D/g, '');
    
    // Formata: (XX) XXXXX-XXXX
    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 7) {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    } else {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhone(e.target.value);
    setPhone(formatted);
  };

  if (showRegister) {
    return <RegisterForm onBack={() => setShowRegister(false)} setIsAuthenticated={setIsAuthenticated} setUser={setUser} />;
  }

  if (showConfirmationModal) {
    return (
      <ConfirmationModal
        phone={confirmationPhone}
        onClose={() => {
          setShowConfirmationModal(false);
          setConfirmationPhone('');
        }}
        onSuccess={() => {
          setShowConfirmationModal(false);
          setConfirmationPhone('');
          setError('Cadastro confirmado com sucesso! Você já pode fazer login.');
        }}
      />
    );
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <div className="login-logo">
            <span className="login-logo-icon">✓</span>
            <span className="login-logo-text">FidHub</span>
          </div>
          <h1>Bem-vindo ao FidHub</h1>
          <p>Entre com seu telefone para acumular pontos e ganhar recompensas</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && (
            <div className="login-error">
              <span>⚠️</span>
              <span>{error}</span>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="phone">Número de Telefone</label>
            <div className="phone-input-group">
              <span className="phone-prefix">+55</span>
              <input
                id="phone"
                type="tel"
                placeholder="(11) 98765-4321"
                value={phone}
                onChange={handlePhoneChange}
                maxLength={15}
                required
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="password">Senha</label>
            <input
              id="password"
              type="password"
              placeholder="Digite sua senha"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
            />
          </div>

          <button type="submit" className="login-button" disabled={loading}>
            {loading ? (
              <>
                <span className="spinner"></span>
                Entrando...
              </>
            ) : (
              'Entrar'
            )}
          </button>

          <div className="login-footer">
            <div className="register-prompt">
              Ainda não tem uma conta?{' '}
              <button
                type="button"
                className="register-link"
                onClick={() => setShowRegister(true)}
                disabled={loading}
              >
                Criar conta
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

interface RegisterFormProps {
  onBack: () => void;
  setIsAuthenticated: (value: boolean) => void;
  setUser: (user: User | null) => void;
}

const RegisterForm: React.FC<RegisterFormProps> = ({ onBack, setIsAuthenticated, setUser }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    cpf: '',
    password: '',
    confirmPassword: '',
    email: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const formatPhone = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 7) {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    } else {
      return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
    }
  };

  const formatCPF = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) {
      return numbers;
    } else if (numbers.length <= 6) {
      return `${numbers.slice(0, 3)}.${numbers.slice(3)}`;
    } else if (numbers.length <= 9) {
      return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6)}`;
    } else {
      return `${numbers.slice(0, 3)}.${numbers.slice(3, 6)}.${numbers.slice(6, 9)}-${numbers.slice(9, 11)}`;
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('As senhas não coincidem.');
      return;
    }

    if (formData.password.length < 8) {
      setError('A senha deve ter pelo menos 8 caracteres.');
      return;
    }

    setLoading(true);

    try {
      const cleanPhone = formData.phone.replace(/\D/g, '');
      const phoneWithCountryCode = `+55${cleanPhone}`;
      const cleanCPF = formData.cpf.replace(/\D/g, '');

      const response = await authService.register({
        name: formData.name,
        phone: phoneWithCountryCode,
        cpf: cleanCPF,
        password: formData.password,
        email: formData.email || undefined
      });

      if (response.requiresConfirmation) {
        setError(`Cadastro realizado! Um código de confirmação foi enviado para ${response.codeDeliveryDestination || 'seu telefone'}. Verifique sua mensagem.`);
        // Redirecionar para página de confirmação ou mostrar modal
        setTimeout(() => {
          onBack();
        }, 3000);
        return;
      }

      authService.setAuth(response.token, response.user);
      setIsAuthenticated(true);
      setUser({
        username: response.user.username || response.user.userId,
      });
      navigate('/my-points');
    } catch (err: any) {
      console.error('Register error:', err);
      setError(err.response?.data?.message || 'Erro ao criar conta. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container register-container">
        <div className="login-header">
          <div className="login-logo">
            <span className="login-logo-icon">✓</span>
            <span className="login-logo-text">FidHub</span>
          </div>
          <h1>Criar Conta</h1>
          <p>Preencha os dados para começar a acumular pontos</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && (
            <div className={`login-error ${error.includes('enviado') ? 'login-success' : ''}`}>
              <span>{error.includes('enviado') ? '✅' : '⚠️'}</span>
              <span>{error}</span>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="name">Nome Completo *</label>
            <input
              id="name"
              type="text"
              placeholder="Seu nome completo"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="register-phone">Telefone *</label>
            <div className="phone-input-group">
              <span className="phone-prefix">+55</span>
              <input
                id="register-phone"
                type="tel"
                placeholder="(11) 98765-4321"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: formatPhone(e.target.value)})}
                maxLength={15}
                required
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="cpf">CPF *</label>
            <input
              id="cpf"
              type="text"
              placeholder="000.000.000-00"
              value={formData.cpf}
              onChange={(e) => setFormData({...formData, cpf: formatCPF(e.target.value)})}
              maxLength={14}
              required
              disabled={loading}
            />
            <span className="form-hint">Apenas números, sem pontos ou traços</span>
          </div>

          <div className="form-group">
            <label htmlFor="email">E-mail (opcional)</label>
            <input
              id="email"
              type="email"
              placeholder="seu@email.com"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              disabled={loading}
            />
          </div>

            <div className="form-group">
              <label htmlFor="password">Senha *</label>
              <input
                id="password"
                type="password"
                placeholder="Mínimo 8 caracteres"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                minLength={8}
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirmar Senha *</label>
              <input
                id="confirmPassword"
                type="password"
                placeholder="Digite a senha novamente"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                minLength={8}
                required
                disabled={loading}
              />
            </div>

          <button type="submit" className="login-button" disabled={loading}>
            {loading ? (
              <>
                <span className="spinner"></span>
                Criando conta...
              </>
            ) : (
              'Criar Conta'
            )}
          </button>

          <div className="login-footer">
            <button
              type="button"
              className="back-to-login-btn"
              onClick={onBack}
              disabled={loading}
            >
              ← Voltar para login
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface ConfirmationModalProps {
  phone: string;
  onClose: () => void;
  onSuccess: () => void;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({ phone, onClose, onSuccess }) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const handleConfirm = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!code || code.length !== 6) {
      setError('Por favor, digite o código de 6 dígitos.');
      return;
    }

    setLoading(true);

    try {
      await authService.confirmCode({
        phone: phone,
        confirmationCode: code
      });
      setSuccessMessage('Cadastro confirmado com sucesso!');
      setTimeout(() => {
        onSuccess();
      }, 1500);
    } catch (err: any) {
      console.error('Confirmation error:', err);
      setError(err.response?.data?.message || 'Erro ao confirmar código. Verifique o código e tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError('');
    setSuccessMessage('');
    setResendLoading(true);

    try {
      await authService.resendConfirmationCode({ phone });
      setSuccessMessage('Código reenviado com sucesso! Verifique seu telefone.');
    } catch (err: any) {
      console.error('Resend error:', err);
      setError(err.response?.data?.message || 'Erro ao reenviar código. Tente novamente.');
    } finally {
      setResendLoading(false);
    }
  };

  const formatPhoneDisplay = (phoneNumber: string) => {
    // Remove +55 e formata para exibição
    const clean = phoneNumber.replace(/^\+55/, '').replace(/\D/g, '');
    if (clean.length === 11) {
      return `(${clean.slice(0, 2)}) ${clean.slice(2, 7)}-${clean.slice(7)}`;
    }
    return phoneNumber;
  };

  return (
    <div className="login-page">
      <div className="login-container confirmation-modal-container">
        <div className="login-header">
          <div className="login-logo">
            <span className="login-logo-icon">✓</span>
            <span className="login-logo-text">FidHub</span>
          </div>
          <h1>Confirmação Necessária</h1>
          <p>Enviamos um código de confirmação por SMS para {formatPhoneDisplay(phone)}</p>
        </div>

        <form onSubmit={handleConfirm} className="login-form">
          {error && (
            <div className="login-error">
              <span>⚠️</span>
              <span>{error}</span>
            </div>
          )}

          {successMessage && (
            <div className="login-error login-success">
              <span>✅</span>
              <span>{successMessage}</span>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="confirmation-code">Código de Confirmação</label>
            <input
              id="confirmation-code"
              type="text"
              placeholder="000000"
              value={code}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                setCode(value);
              }}
              maxLength={6}
              required
              disabled={loading}
              style={{ textAlign: 'center', letterSpacing: '8px', fontFamily: 'monospace', fontSize: '24px', fontWeight: 'bold' }}
            />
            <span className="form-hint">Digite o código de 6 dígitos recebido por SMS</span>
          </div>

          <button type="submit" className="login-button" disabled={loading || resendLoading}>
            {loading ? (
              <>
                <span className="spinner"></span>
                Confirmando...
              </>
            ) : (
              'Confirmar Código'
            )}
          </button>

          <div className="login-footer">
            <button
              type="button"
              className="resend-code-link"
              onClick={handleResendCode}
              disabled={loading || resendLoading}
            >
              {resendLoading ? 'Enviando...' : 'Reenviar código'}
            </button>
            <button
              type="button"
              className="back-to-login-btn"
              onClick={onClose}
              disabled={loading || resendLoading}
            >
              ← Voltar para login
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;
